/**
 * Verkleinert ein Foto im Browser, bevor es hochgeladen wird.
 *
 * Am 21.10.2026 lief die Platte des Prod-Servers voll. Drei Viertel des Platzes
 * belegten Fotos von Nachweisen (Einverständniserklärung, Kursnachweis,
 * Vollmacht): rund 9.900 Handyaufnahmen mit im Schnitt 2,4 MB. Lesbar bleibt
 * ein solches Dokument auch mit 2000 Pixeln Kantenlänge als JPEG — dann sind es
 * 300 bis 500 KB.
 *
 * Verkleinert werden nur JPEG und PNG. PDF bleibt unangetastet (dafür bräuchte
 * es eine eigene Bibliothek, und die PDFs sind im Schnitt ohnehin klein), GIF
 * ebenso (kann bewegt sein). Kleine Dateien gehen unverändert durch.
 *
 * **Scheitert irgendetwas, geht die Originaldatei hoch.** Die Verkleinerung ist
 * eine Ersparnis, keine Voraussetzung — ein Nachweis darf nicht daran
 * scheitern, dass ein älterer Browser ein Bild nicht zeichnen kann.
 */
export const SHRINK_TYPES = ['image/jpeg', 'image/png']

/** Längste Kante nach dem Verkleinern. */
export const SHRINK_MAX_EDGE = 2000

/** JPEG-Qualität: 0,8 hält Schrift auf einem fotografierten Blatt scharf. */
export const SHRINK_QUALITY = 0.8

/** Darunter lohnt sich das Neuberechnen nicht. */
export const SHRINK_MIN_BYTES = 400 * 1024

/**
 * Zielgröße bei gleichem Seitenverhältnis. Vergrößert wird nie.
 *
 * @returns {{width: number, height: number}}
 */
export const zielMasse = (width, height, maxEdge = SHRINK_MAX_EDGE) => {
	const faktor = Math.min(1, maxEdge / Math.max(width, height))
	return { width: Math.round(width * faktor), height: Math.round(height * faktor) }
}

/** „Scan.PNG" → „Scan.jpg", „foto" → „foto.jpg". */
export const jpegName = name => `${String(name || 'bild').replace(/\.[^./\\]+$/, '')}.jpg`

/** Muss diese Datei überhaupt angefasst werden? */
export const sollVerkleinern = file =>
	Boolean(file) && SHRINK_TYPES.includes(file.type) && file.size >= SHRINK_MIN_BYTES

/*
 * Über ein <img> statt über createImageBitmap: Seit 2020 dreht jeder gängige
 * Browser beim Zeichnen eines <img> nach der EXIF-Ausrichtung — ein
 * hochkant fotografiertes Blatt bleibt hochkant. Die Optionen von
 * createImageBitmap kennt Safari erst spät.
 */
const ladeBild = file =>
	new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file)
		const bild = new Image()
		bild.onload = () => {
			URL.revokeObjectURL(url)
			resolve(bild)
		}
		bild.onerror = () => {
			URL.revokeObjectURL(url)
			reject(new Error('Bild nicht lesbar'))
		}
		bild.src = url
	})

const alsBlob = (canvas, type, quality) =>
	new Promise(resolve => {
		canvas.toBlob(resolve, type, quality)
	})

/**
 * @param {File} file
 * @returns {Promise<File>} die verkleinerte Datei — oder das Original, wenn es
 *   nichts zu gewinnen gibt oder etwas scheitert
 */
export const verkleinereBild = async file => {
	if (!sollVerkleinern(file)) return file

	try {
		const bild = await ladeBild(file)
		const { width, height } = zielMasse(bild.naturalWidth, bild.naturalHeight)
		if (!width || !height) return file

		const canvas = document.createElement('canvas')
		canvas.width = width
		canvas.height = height
		const ctx = canvas.getContext('2d')
		// Durchsichtige Stellen eines PNG würden in JPEG schwarz.
		ctx.fillStyle = '#fff'
		ctx.fillRect(0, 0, width, height)
		ctx.imageSmoothingQuality = 'high'
		ctx.drawImage(bild, 0, 0, width, height)

		const blob = await alsBlob(canvas, 'image/jpeg', SHRINK_QUALITY)
		if (!blob || blob.size >= file.size) return file

		return new File([blob], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified })
	} catch (e) {
		return file
	}
}
